import { createFileRoute } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AuthRequired } from "@/components/auth/AuthRequired";
import { SubPage } from "@/components/profile/SettingsKit";
import { fetchProfile, updateProfile, uploadAvatar, removeAvatar, keys } from "@/lib/queries";
import { humanizeError } from "@/lib/errorMessages";
import { Avatar, Input, Button, Spinner } from "@/components/ui";
import { Camera } from "lucide-react";
import { buildHead } from "@/lib/seo";

export const Route = createFileRoute("/_app/settings_/edit")({
  head: () =>
    buildHead({ path: "/settings/edit", title: "Edit profile", noindex: true }),
  component: () => (
    <AuthRequired>
      <EditProfile />
    </AuthRequired>
  ),
});

function EditProfile() {
  const { t } = useTranslation("account");
  const qc = useQueryClient();
  const profile = useQuery({ queryKey: keys.profile(), queryFn: fetchProfile });
  const fileRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const displayName = name ?? profile.data?.display_name ?? "";
  const refresh = () => qc.invalidateQueries({ queryKey: keys.profile() });

  const upload = useMutation({ mutationFn: (f: File) => uploadAvatar(f), onSuccess: refresh, onError: (e) => setErr(humanizeError(e)) });
  const remove = useMutation({ mutationFn: () => removeAvatar(), onSuccess: refresh });
  const save = useMutation({
    mutationFn: () => updateProfile({ display_name: displayName.trim() }),
    onSuccess: () => {
      setOk(true);
      refresh();
    },
    onError: (e) => setErr(humanizeError(e)),
  });

  return (
    <SubPage title={t("edit.title")}>
      <div className="flex flex-col items-center">
        <div className="relative">
          <Avatar src={profile.data?.avatar_url} name={displayName || t("edit.you")} size={104} className="rounded-full" />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            aria-label={t("form.changePhoto")}
            className="absolute bottom-0 right-0 grid h-8 w-8 place-items-center rounded-full border-2 border-bg bg-primary text-primary-fg transition-opacity hover:opacity-90"
          >
            {upload.isPending ? <Spinner size={14} /> : <Camera className="h-4 w-4" />}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) {
                setErr(null);
                upload.mutate(f);
              }
              e.target.value = "";
            }}
          />
        </div>
        {profile.data?.avatar_url && (
          <button type="button" onClick={() => remove.mutate()} className="mt-2 text-xs font-medium text-fg-muted transition-colors hover:text-danger">
            {remove.isPending ? t("edit.removingPhoto") : t("edit.removePhoto")}
          </button>
        )}
      </div>

      <div className="mx-auto mt-6 max-w-md">
        <label htmlFor="dn" className="mb-1.5 block text-sm font-medium">
          {t("form.displayName")}
        </label>
        <Input
          id="dn"
          value={displayName}
          onChange={(e) => {
            setName(e.target.value);
            setOk(false);
          }}
          placeholder={t("edit.namePlaceholder")}
          maxLength={40}
        />
        <p className="mt-1.5 text-xs text-fg-muted">{t("edit.nameHint")}</p>
        {err && <p className="mt-2 text-sm text-danger">{err}</p>}
        {ok && <p className="mt-2 text-sm text-success">{t("edit.profileSaved")}</p>}
        <Button
          className="mt-4"
          block
          onClick={() => {
            setErr(null);
            save.mutate();
          }}
          loading={save.isPending}
          disabled={!displayName.trim()}
        >
          {t("form.saveChanges")}
        </Button>
      </div>
    </SubPage>
  );
}
